import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wind, Hand, Zap, Activity, Heart, Shield, ArrowLeft, Play, Pause, RotateCcw, Star } from "lucide-react";
import BreathworkAnimation from './BreathworkAnimation';
interface NervousSystemResetProps {
  isOpen: boolean;
  onClose: () => void;
}
type RegulationState = 'anxious' | 'dissociated' | 'overwhelmed' | 'low-energy' | 'calm';
type ToolType = 'breathwork' | 'eft' | 'vagal' | 'movement' | 'coherence' | 'safe-space';
const NervousSystemReset: React.FC<NervousSystemResetProps> = ({
  isOpen,
  onClose
}) => {
  const [currentTool, setCurrentTool] = useState<ToolType | null>(null);
  const [beforeState, setBeforeState] = useState<RegulationState | null>(null);
  const [afterState, setAfterState] = useState<RegulationState | null>(null);
  const [favorites, setFavorites] = useState<Set<ToolType>>(new Set());
  if (!isOpen) return null;
  const tools = [{
    id: 'breathwork' as ToolType,
    title: 'Breathwork',
    icon: Wind,
    description: 'Regulate your nervous system with guided breathing patterns',
    color: 'bg-gradient-primary'
  }, {
    id: 'eft' as ToolType,
    title: 'EFT Tapping',
    icon: Hand,
    description: 'Emotional Freedom Technique for anxiety and grounding',
    color: 'bg-gradient-mystical'
  }, {
    id: 'vagal' as ToolType,
    title: 'Vagal Toning',
    icon: Zap,
    description: 'Humming exercises to stimulate your vagus nerve',
    color: 'bg-secondary'
  }, {
    id: 'movement' as ToolType,
    title: 'Shaking Reset',
    icon: Activity,
    description: 'Somatic shaking to discharge stored tension',
    color: 'bg-accent'
  }, {
    id: 'coherence' as ToolType,
    title: 'Heart Coherence',
    icon: Heart,
    description: 'Connect with your heart center through breath and gratitude',
    color: 'bg-soft-accent'
  }, {
    id: 'safe-space' as ToolType,
    title: 'Safe Space',
    icon: Shield,
    description: 'Guided visualisation for safety and grounding',
    color: 'bg-muted'
  }];
  const regulationStates = [{
    value: 'anxious',
    label: 'Anxious'
  }, {
    value: 'dissociated',
    label: 'Dissociated'
  }, {
    value: 'overwhelmed',
    label: 'Overwhelmed'
  }, {
    value: 'low-energy',
    label: 'Low Energy'
  }, {
    value: 'calm',
    label: 'Calm'
  }];
  const toggleFavorite = (toolId: ToolType) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(toolId)) {
      newFavorites.delete(toolId);
    } else {
      newFavorites.add(toolId);
    }
    setFavorites(newFavorites);
  };
  const renderToolContent = () => {
    switch (currentTool) {
      case 'breathwork':
        return <BreathworkTool onBack={() => setCurrentTool(null)} />;
      case 'eft':
        return <EFTTappingTool onBack={() => setCurrentTool(null)} />;
      case 'vagal':
        return <VagalToningTool onBack={() => setCurrentTool(null)} />;
      case 'movement':
        return <MovementResetTool onBack={() => setCurrentTool(null)} />;
      case 'coherence':
        return <HeartCoherenceTool onBack={() => setCurrentTool(null)} />;
      case 'safe-space':
        return <SafeSpaceTool onBack={() => setCurrentTool(null)} />;
      default:
        return null;
    }
  };
  return <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-border shadow-xl">
        <div className="p-4 border-b flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Nervous System Reset</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>

        <div className="p-6">
          {currentTool ? renderToolContent() : <>
              {/* Header */}
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  Because alignment isn't just mindset
                </h3>
                <p className="text-muted-foreground">
                  It's your physiology, your frequency, and your field.
                </p>
              </div>

              {/* Regulation Tracker */}
              <Card className="p-4 mb-6 bg-muted/30">
                <h4 className="font-semibold mb-3 text-sm">Regulation Tracker (Optional)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">How were you feeling before you began the reset?</label>
                    <Select value={beforeState || ''} onValueChange={value => setBeforeState(value as RegulationState)}>
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Select state..." />
                      </SelectTrigger>
                      <SelectContent>
                        {regulationStates.map(state => <SelectItem key={state.value} value={state.value}>
                            {state.label}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">
                      How do you feel now?
                    </label>
                    <Select value={afterState || ''} onValueChange={value => setAfterState(value as RegulationState)}>
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Select state..." />
                      </SelectTrigger>
                      <SelectContent>
                        {regulationStates.map(state => <SelectItem key={state.value} value={state.value}>
                            {state.label}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Card>

              {/* Tools Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tools.map(tool => {
              const IconComponent = tool.icon;
              const isFavorite = favorites.has(tool.id);
              return <Card key={tool.id} className="p-4 hover:shadow-gentle transition-all duration-300 hover:scale-105 cursor-pointer relative" onClick={() => setCurrentTool(tool.id)}>
                      <button onClick={e => {
                  e.stopPropagation();
                  toggleFavorite(tool.id);
                }} className="absolute top-2 right-2 p-1 hover:bg-muted rounded-full">
                        <Star className={`h-4 w-4 ${isFavorite ? 'fill-primary text-primary' : 'text-muted-foreground'}`} />
                      </button>
                      
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 ${tool.color} rounded-full flex items-center justify-center`}>
                          <IconComponent className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <h3 className="font-semibold text-foreground">{tool.title}</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {tool.description}
                      </p>
                    </Card>;
            })}
              </div>

              {/* Favourites Section */}
              {favorites.size > 0 && <div className="mt-6">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Your Favourites</h4>
                  <div className="flex flex-wrap gap-2">
                    {Array.from(favorites).map(toolId => {
                const tool = tools.find(t => t.id === toolId);
                if (!tool) return null;
                return <Badge key={toolId} className="cursor-pointer hover:bg-primary/20" onClick={() => setCurrentTool(toolId)}>
                          {tool.title}
                        </Badge>;
              })}
                  </div>
                </div>}
            </>}
        </div>
      </div>
    </div>;
};

// Individual Tool Components
const BreathworkTool: React.FC<{
  onBack: () => void;
}> = ({
  onBack
}) => {
  return <div>
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-xl font-semibold">Breathwork Practice</h3>
      </div>
      <BreathworkAnimation />
    </div>;
};
const EFTTappingTool: React.FC<{
  onBack: () => void;
}> = ({
  onBack
}) => {
  const eftVideos = [{
    title: "EFT for Anxiety",
    description: "Quick tapping sequence to calm anxious thoughts"
  }, {
    title: "EFT to Reconnect With Self",
    description: "Restore your connection to inner wisdom"
  }, {
    title: "EFT for Grounding + Safety",
    description: "Feel safe and grounded in your body"
  }];
  return <div>
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-xl font-semibold">EFT Tapping</h3>
      </div>
      
      <p className="text-muted-foreground mb-6">
        Emotional Freedom Technique uses gentle tapping on meridian points to release emotional blocks and restore balance.
      </p>

      <div className="space-y-4">
        {eftVideos.map((video, index) => <Card key={index} className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">{video.title}</h4>
                <p className="text-sm text-muted-foreground">{video.description}</p>
              </div>
              <Button size="sm">
                <Play className="h-4 w-4 mr-1" />
                Watch
              </Button>
            </div>
          </Card>)}
      </div>
    </div>;
};
const VagalToningTool: React.FC<{
  onBack: () => void;
}> = ({
  onBack
}) => {
  const [isHumming, setIsHumming] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(120); // 2 minutes

  return <div>
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-xl font-semibold">Vagal Toning & Humming</h3>
      </div>

      <Card className="p-6 text-center">
        <Zap className="h-12 w-12 mx-auto mb-4 text-primary" />
        <h4 className="text-lg font-semibold mb-2">Hum Your Way to Calm</h4>
        <p className="text-muted-foreground mb-6">
          Humming on your exhale stimulates the vagus nerve, activating your body's natural relaxation response.
        </p>
        
        <div className="bg-muted/50 p-4 rounded-lg mb-6">
          <p className="text-sm">
            <strong>Instructions:</strong><br />
            1. Take a comfortable breath in<br />
            2. As you exhale, make a gentle humming sound<br />
            3. Feel the vibration in your chest and throat<br />
            4. Continue for 2 minutes
          </p>
        </div>

        <div className="text-2xl font-bold mb-4">
          {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
        </div>

        <div className="flex justify-center gap-2">
          <Button onClick={() => setIsHumming(!isHumming)} className="flex items-center gap-2">
            {isHumming ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {isHumming ? 'Pause' : 'Start Humming'}
          </Button>
          <Button variant="outline" onClick={() => setTimeRemaining(120)}>
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    </div>;
};
const MovementResetTool: React.FC<{
  onBack: () => void;
}> = ({
  onBack
}) => {
  return <div>
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-xl font-semibold">Shaking & Movement Reset</h3>
      </div>

      <Card className="p-6">
        <Activity className="h-12 w-12 mx-auto mb-4 text-primary" />
        <h4 className="text-lg font-semibold mb-4 text-center">Discharge Stored Tension</h4>
        
        <div className="space-y-4">
          <div className="bg-muted/50 p-4 rounded-lg">
            <h5 className="font-medium mb-2">Why Shaking Works</h5>
            <p className="text-sm text-muted-foreground">
              Animals naturally shake after trauma to discharge excess energy. This somatic practice helps 
              release tension stored in your nervous system.
            </p>
          </div>

          <div className="bg-muted/30 p-4 rounded-lg">
            <h5 className="font-medium mb-2">Instructions</h5>
            <ol className="text-sm space-y-1 text-muted-foreground">
              <li>1. Stand with feet shoulder-width apart</li>             
              <li>2. Begin by gently bouncing on your feet</li>
              <li>3. Let the shaking spread naturally through your body</li>
              <li>4. Shake your hands, arms, shoulders, and whole body</li>
              <li>5. Continue for 2-5 minutes or until you feel complete</li>
              <li>6. End by standing still and noticing how you feel</li>
            </ol>
          </div>

          <Button className="w-full">
            <Play className="h-4 w-4 mr-2" />
            Watch Guided Video
          </Button>
        </div>
      </Card>
    </div>;
};
const HeartCoherenceTool: React.FC<{
  onBack: () => void;
}> = ({
  onBack
}) => {
  return <div>
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-xl font-semibold">Quick Heart Coherence</h3>
      </div>

      <Card className="p-6 text-center">
        <Heart className="h-12 w-12 mx-auto mb-4 text-primary" />
        <h4 className="text-lg font-semibold mb-4">Connect With Your Heart Center</h4>
        
        <div className="bg-gradient-primary/10 p-6 rounded-lg mb-6">
          <p className="text-sm mb-4">
            Place your hand on your heart. Feel its rhythm. This is your center of wisdom and love.
          </p>
          
          <div className="text-left space-y-3">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-xs text-primary-foreground font-bold">1</div>
              <p className="text-sm">Drop your attention into your heart space</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-xs text-primary-foreground font-bold">2</div>
              <p className="text-sm">Breathe slowly: In for 5 counts, out for 5 counts</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-xs text-primary-foreground font-bold">3</div>
              <p className="text-sm">Recall a moment of genuine gratitude or appreciation</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-xs text-primary-foreground font-bold">4</div>
              <p className="text-sm">Allow this feeling to expand through your whole being</p>
            </div>
          </div>
        </div>

        <Button className="w-full mb-4">
          <Play className="h-4 w-4 mr-2" />
          Start Guided Practice
        </Button>
        
        <p className="text-xs text-muted-foreground">
          Practice for 3-5 minutes daily to build heart coherence
        </p>
      </Card>
    </div>;
};
const SafeSpaceTool: React.FC<{
  onBack: () => void;
}> = ({
  onBack
}) => {
  return <div>
      <div className="flex items-center gap-2 mb-4">        
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-xl font-semibold">Safe Space Meditation</h3>
      </div>

      <Card className="p-6">
        <Shield className="h-12 w-12 mx-auto mb-4 text-primary" />
        <h4 className="text-lg font-semibold mb-4 text-center">Create Your Inner Sanctuary</h4>
        
        <div className="space-y-4">          
          <div className="bg-muted/30 p-4 rounded-lg">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Close your eyes and imagine a place where you feel completely safe and at peace. 
              It might be a cozy room, a peaceful garden, or somewhere from your memory. 
              Notice the colors, sounds, and feelings in this space. 
              This is your inner sanctuary - you can return here anytime you need to feel grounded and secure.
            </p>
          </div>

          <div className="bg-gradient-mystical/10 p-4 rounded-lg">
            <h5 className="font-medium mb-2 text-center">Affirmations for Safety</h5>
            <div className="space-y-2 text-sm text-center">
              <p>"I am safe in this moment"</p>
              <p>"I trust my inner wisdom"</p>
              <p>"I am surrounded by love and protection"</p>
              <p>"I have everything I need within me"</p>
            </div>
          </div>

          <Button className="w-full">
            <Play className="h-4 w-4 mr-2" />
            Start Guided Meditation
          </Button>
        </div>
      </Card>
    </div>;
};
export default NervousSystemReset;